import { z } from "zod";
import {
  MAX_JOURNEY_DAYS,
  MAX_SENTENCES,
  MAX_WORDS,
  MIN_SENTENCES,
  MIN_WORDS,
} from "./constants";

function isValidIanaTimeZone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone: timezone }).format();
    return true;
  } catch {
    return false;
  }
}

export const languageCodeSchema = z.enum(["en", "nl", "th"]);

export const sheetSettingsSchema = z
  .object({
    left: languageCodeSchema,
    right: languageCodeSchema,
    leftLabel: z.string().trim().max(24),
    rightLabel: z.string().trim().max(24),
    words: z.number().int().min(MIN_WORDS).max(MAX_WORDS),
    sentences: z.number().int().min(MIN_SENTENCES).max(MAX_SENTENCES),
    showPronunciation: z.boolean(),
    timezone: z.string().min(1),
    startDate: z.iso.date(),
  })
  .superRefine((settings, context) => {
    if (settings.left === settings.right) {
      context.addIssue({
        code: "custom",
        path: ["right"],
        message: "Pick two different languages.",
      });
    }

    if (!isValidIanaTimeZone(settings.timezone)) {
      context.addIssue({
        code: "custom",
        path: ["timezone"],
        message: "Enter a valid IANA timezone, such as Asia/Bangkok.",
      });
    }
  });

export const persistedAppStateSchema = z.object({
  schemaVersion: z.literal(2),
  settings: sheetSettingsSchema.nullable(),
  progress: z.object({
    completedDays: z
      .array(z.number().int().min(1).max(MAX_JOURNEY_DAYS))
      .refine((days) => new Set(days).size === days.length, {
        message: "Completed days must be unique.",
      }),
  }),
  access: z.object({
    unlocked: z.boolean(),
    unlockedAt: z.iso.datetime().nullable(),
    // Added after schema 2 shipped. It defaults rather than being required so
    // an envelope saved before the early list still hydrates untouched.
    email: z.email().nullable().default(null),
  }),
});

const localizedFormSchema = z.object({
  text: z.string().trim().min(1),
  pronunciation: z.string().trim().min(1).optional(),
  note: z.string().trim().min(1).optional(),
  speakerVariants: z
    .array(
      z.object({
        speaker: z.enum(["masculine", "feminine"]),
        text: z.string().trim().min(1),
        usage: z.string().trim().min(1),
      }),
    )
    .min(2)
    .optional(),
});

const localizedFormsSchema = z.object({
  en: localizedFormSchema,
  nl: localizedFormSchema,
  th: localizedFormSchema,
});

const reviewRecordSchema = z.object({
  status: z.enum(["machine-reviewed", "human-reviewed"]),
  reviewedBy: z.string().min(1),
  reviewedAt: z.iso.date(),
  source: z.string().min(1),
});

const languageReviewsSchema = z.object({
  en: reviewRecordSchema,
  nl: reviewRecordSchema,
  th: reviewRecordSchema,
  romanization: reviewRecordSchema,
});

const vocabularyItemSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  meaningId: z.string().regex(/^[a-z0-9-]+$/),
  priority: z.number().int().positive(),
  forms: localizedFormsSchema,
  reviews: languageReviewsSchema,
});

const sentenceSegmentSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  forms: localizedFormsSchema,
  pronunciation: z.string().min(1).optional(),
  vocabularyItemId: z.string().regex(/^[a-z0-9-]+$/).optional(),
});

const sentenceSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  meaningId: z.string().regex(/^[a-z0-9-]+$/),
  priority: z.number().int().positive(),
  forms: localizedFormsSchema,
  highlightedVocabularyIds: z.array(z.string()),
  breakdown: z.array(sentenceSegmentSchema).optional(),
  reviews: languageReviewsSchema,
});

export const lessonSchema = z.object({
  id: z.string().regex(/^starter-\d{2}-[a-z0-9-]+$/),
  day: z.number().int().min(1).max(30),
  slug: z.string().regex(/^[a-z0-9-]+$/),
  emoji: z.string().min(1),
  title: z.object({
    en: z.string().min(1),
    nl: z.string().min(1),
    th: z.string().min(1),
  }),
  summary: z.string().min(1),
  publicationStatus: z.enum(["preview", "published"]),
  provenance: z.string().min(1),
  vocabulary: z.array(vocabularyItemSchema).min(5),
  sentences: z.array(sentenceSchema).min(2),
});

export const curriculumManifestSchema = z.object({
  id: z.literal("starter-30"),
  version: z.string().min(1),
  title: z.string().min(1),
  supportedLanguages: z.array(languageCodeSchema).length(3),
  publishedAt: z.iso.date(),
  lessons: z
    .array(
      z.object({
        id: z.string().min(1),
        day: z.number().int().min(1).max(30),
        slug: z.string().min(1),
        emoji: z.string().min(1),
        title: z.object({
          en: z.string().min(1),
          nl: z.string().min(1),
          th: z.string().min(1),
        }),
        publicationStatus: z.enum(["preview", "published"]),
        vocabularyCount: z.number().int().min(5),
        sentenceCount: z.number().int().min(2),
      }),
    )
    .length(30),
});

const articleBlockSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("paragraph"), text: z.string().trim().min(1) }),
  z.object({ kind: z.literal("heading"), text: z.string().trim().min(1) }),
  z.object({ kind: z.literal("subheading"), text: z.string().trim().min(1) }),
  z.object({
    kind: z.literal("list"),
    ordered: z.boolean().optional(),
    items: z.array(z.string().trim().min(1)).min(2),
  }),
  z.object({
    kind: z.literal("table"),
    caption: z.string().trim().min(1).optional(),
    columns: z.array(z.string().trim().min(1)).min(2),
    rows: z.array(z.array(z.string().trim())).min(1),
  }),
  z.object({
    kind: z.literal("callout"),
    title: z.string().trim().min(1),
    text: z.string().trim().min(1),
  }),
]);

/**
 * An answer that only exists in structured data is a lie told to a machine, so
 * the same text has to survive being read on the page: no markup, no links, and
 * long enough to stand on its own once it is quoted somewhere else.
 */
const PLAIN_FAQ_TEXT = /[\r\n]|<\/?[a-z][^>]*>|[*_`#]|\[[^\]]*\]|(?:https?:\/\/|www\.)\S+/i;

const articleFaqSchema = z.object({
  question: z
    .string()
    .trim()
    .min(10)
    .max(180)
    .refine((value) => value.endsWith("?"), "must end with a question mark")
    .refine((value) => !PLAIN_FAQ_TEXT.test(value), "must be plain text"),
  answer: z
    .string()
    .trim()
    .min(60)
    .max(900)
    .refine((value) => !PLAIN_FAQ_TEXT.test(value), "must be plain text"),
});

const articleSourceSchema = z.object({
  id: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  authority: z.string().trim().min(1),
  title: z.string().trim().min(1),
  url: z.url().refine((value) => value.startsWith("https://"), "must use HTTPS"),
  lastChecked: z.iso.date(),
  nextReview: z.iso.date(),
});

export const articleSchema = z
  .object({
    slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    title: z.string().trim().min(1).max(90),
    // Long descriptions are truncated in a result list, which is the one place
    // this string exists to work, so the cap is the useful length not a limit.
    description: z.string().trim().min(70).max(160),
    lede: z.string().trim().min(80),
    emoji: z.string().min(1),
    category: z.string().trim().min(1),
    date: z.iso.date(),
    updated: z.iso.date(),
    primaryKeyword: z.string().trim().min(1),
    secondaryKeywords: z.array(z.string().trim().min(1)),
    tags: z.array(z.string().trim().min(1)).min(1),
    ctaLabel: z.string().trim().min(1),
    ctaHref: z.string().regex(/^\/(?!\/)/, "must be a local path"),
    related: z.array(z.string().min(1)),
    body: z.array(articleBlockSchema).min(6),
    faqs: z.array(articleFaqSchema).min(3).max(12),
    sources: z.array(articleSourceSchema).min(1),
    author: z.string().trim().min(1).nullable(),
    noindex: z.boolean(),
    publicationStatus: z.enum(["preview", "published"]),
    review: reviewRecordSchema,
  })
  .superRefine((article, context) => {
    if (article.updated < article.date) {
      context.addIssue({
        code: "custom",
        path: ["updated"],
        message: "must not precede the publication date.",
      });
    }

    if (article.related.includes(article.slug)) {
      context.addIssue({
        code: "custom",
        path: ["related"],
        message: "must not point back at the article itself.",
      });
    }
  });

const apiMetaSchema = z.object({
  version: z.string().min(1),
  previewContent: z.boolean(),
});

export const curriculumManifestEnvelopeSchema = z.object({
  data: curriculumManifestSchema,
  meta: apiMetaSchema,
});

export const lessonEnvelopeSchema = z.object({
  data: lessonSchema,
  meta: apiMetaSchema,
});
